import { apiService } from "api/apiService";
import type {
  TeacherPublicPortfolioTaskShallowResource,
  PublicPortfolioTaskResource,
  PortfolioTaskTemplateResource,
  FilterResource,
} from "api/generated";
import {
  teacherPublicLibraryTaskFromDto,
  publicLibraryTaskFromDto,
  libraryTaskFromDto,
} from "portfolio/api/mappers";
import type {
  TeacherPublicLibraryTask,
  PublicLibraryTask,
  LibraryTask,
  TaskDto,
} from "portfolio/types";
import type { PaginatedResponse, PaginatedResult } from "api/types";
import type { Filter } from "shared/types";
import { mapPaginatedResponse } from "api/mappers/pagination.mapper";
import { mapFilters } from "shared/mappers";

export const fetchPublicLibraryTasks = async (
  params: Record<string, string> = {},
): Promise<PaginatedResult<TeacherPublicLibraryTask>> => {
  const { data } = await apiService.get<
    PaginatedResponse<TeacherPublicPortfolioTaskShallowResource>
  >("api/portfolio/tasks/public", { params });

  return mapPaginatedResponse(data, teacherPublicLibraryTaskFromDto);
};

export const fetchPublicLibraryTasksFilters = async (): Promise<Filter[]> => {
  const {
    data: { data: filters },
  } = await apiService.get<{ data: FilterResource[] }>(
    "api/portfolio/tasks/public/filters",
  );

  return mapFilters(filters);
};

/**
 * Fetch a public library task (public_task) by ID
 * Endpoint: GET /portfolio/public-tasks/{publicPortfolioTask}
 */
export const fetchPublicLibraryTaskById = async (
  taskId: number,
): Promise<PublicLibraryTask> => {
  const { data: task } = await apiService.get<PublicPortfolioTaskResource>(
    `api/portfolio/public-tasks/${taskId}`,
  );

  return publicLibraryTaskFromDto(task);
};

export const createPublicTask = async (
  dto: TaskDto,
): Promise<PublicLibraryTask> => {
  const { data: task } = await apiService.post<PublicPortfolioTaskResource>(
    "api/portfolio/public-tasks",
    dto,
  );

  return publicLibraryTaskFromDto(task);
};

export const updatePublicTask = async (
  taskId: number,
  dto: TaskDto,
): Promise<PublicLibraryTask> => {
  const { data: task } = await apiService.put<PublicPortfolioTaskResource>(
    `api/portfolio/public-tasks/${taskId}`,
    dto,
  );

  return publicLibraryTaskFromDto(task);
};

export const deletePublicLibraryTask = async (
  taskId: number,
): Promise<void> => {
  await apiService.delete(`api/portfolio/public-tasks/${taskId}`);
};

export const createPublicLibraryTaskAttachments = async (
  taskId: number,
  files: File[],
  onUploadProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<PublicLibraryTask> => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append("attachments[]", file);
  });

  const { data: task } = await apiService.post<PublicPortfolioTaskResource>(
    `api/portfolio/public-tasks/${taskId}/attachments`,
    formData,
    {
      signal,
      onUploadProgress: (event) => {
        if (!onUploadProgress || !event.total) {
          return;
        }
        onUploadProgress(Math.round((event.loaded / event.total) * 100));
      },
    },
  );

  return publicLibraryTaskFromDto(task);
};

export const uploadPublicLibraryTaskBanner = async (
  taskId: number,
  file: File,
): Promise<{ message: string }> => {
  const formData = new FormData();
  formData.append("banner_image", file);

  const { data } = await apiService.post<{ message: string }>(
    `api/portfolio/public-tasks/${taskId}/banner`,
    formData,
  );

  return data;
};

export const deletePublicLibraryTaskBanner = async (
  taskId: number,
): Promise<{ message: string }> => {
  const { data } = await apiService.delete<{ message: string }>(
    `api/portfolio/public-tasks/${taskId}/banner`,
  );

  return data;
};

export const copyPublicTaskToLibrary = async (
  publicTaskId: number,
): Promise<LibraryTask> => {
  const { data: task } = await apiService.post<PortfolioTaskTemplateResource>(
    `api/portfolio/public-tasks/${publicTaskId}/create-template`,
  );

  return libraryTaskFromDto(task);
};
